import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { ScrollLink } from './ScrollLink';
import authService from '../services/authService';
import GoogleSignInButton from './auth/GoogleSignInButton';
import {
  emailValidationMessage,
  normalizeEmail,
} from '../utils/emailValidation';
import '../styles/Cadastro.css';

const Cadastro = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [emailAlreadyRegistered, setEmailAlreadyRegistered] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const validatePassword = (password) => {
    return password.length >= 6 && password.length <= 72;
  };

  const handleInputChange = (field, value) => {
    setErrors(prev => ({ ...prev, [field]: '' }));
    
    switch (field) {
      case 'email':
        setEmail(value);
        setEmailAlreadyRegistered(false);
        if (value && emailValidationMessage(value)) {
          setErrors(prev => ({ ...prev, email: emailValidationMessage(value) }));
        }
        break;
      case 'password':
        setPassword(value);
        if (value && !validatePassword(value)) {
          setErrors(prev => ({ ...prev, password: 'A senha deve ter pelo menos 6 caracteres.' }));
        }
        break;
      default:
        break;
    }
  };
  const navigate = useNavigate();

  const handleRegister = async (e) => {
    e.preventDefault();
    setMessage('');
    setErrors({});
    setEmailAlreadyRegistered(false);
    
    // Validações finais
    const newErrors = {};

    const emailError = emailValidationMessage(email);
    if (emailError) {
      newErrors.email = emailError;
    }
    
    if (!password || !validatePassword(password)) {
      newErrors.password = 'A senha deve ter pelo menos 6 caracteres.';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);

    try {
      await authService.register(normalizeEmail(email), password);
      navigate(`/login?email=${encodeURIComponent(email.trim())}`, {
        replace: true,
        state: { registrationSuccess: true },
      });
    } catch (error) {
      const responseData = error.response?.data;
      const responseErrors = responseData?.errors || {};
      const firstError = (field) => (
        Array.isArray(responseErrors[field]) ? responseErrors[field][0] : responseErrors[field]
      );
      const existingAccount = responseData?.code === 'email_already_registered';

      if (existingAccount) {
        setEmailAlreadyRegistered(true);
        setErrors({});
        setMessage('');
      } else if (Object.keys(responseErrors).length > 0) {
        setErrors({
          email: firstError('email') || '',
          password: firstError('password') || '',
        });
        setMessage('');
      } else if (responseData?.error || responseData?.message) {
        setMessage(responseData.error || responseData.message);
      } else {
        setMessage('Erro de conexão. Verifique sua internet e tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleCredential = async (credential) => {
    setMessage('');
    setErrors({});
    setLoading(true);
    try {
      await authService.googleLogin(credential);
      navigate('/meus-cursos', { replace: true });
      window.location.reload();
    } catch (error) {
      setMessage(error.response?.data?.error || 'Não foi possível continuar com o Google.');
      setLoading(false);
    }
  };

  return (
    <div className="cadastro-page">
      {/* Hero Section */}
      <section className="cadastro-hero">
        <div className="hero-content">
          <h1>Inscreva-se e comece a aprender</h1>
          <p className="hero-subtitle">
            Junte-se a mais de 1,4 milhão de alunos que já transformaram suas carreiras conosco.
          </p>
        </div>
      </section>

      <div className="cadastro-container">
        <div className="cadastro-main">
          {/* Formulário de Cadastro */}
          <div className="cadastro-form-section">
            <div className="form-card">
              <div className="form-header">
                <h2>Criar Conta Gratuita</h2>
                <p>Preencha os dados abaixo para começar</p>
              </div>

              <GoogleSignInButton
                onCredential={handleGoogleCredential}
                onError={setMessage}
                disabled={loading}
                dividerLabel="ou cadastre-se com e-mail"
              />

              <form className="cadastro-form" onSubmit={handleRegister}>
                <div className="input-group">
                  <div className="input-container">
                    <input
                      id="cadastro-email"
                      name="email"
                      type="email"
                      placeholder="E-mail"
                      value={email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      required
                      maxLength="255"
                      className={errors.email ? 'error' : ''}
                      autoComplete="email"
                    />
                    {errors.email && <span className="error-text">{errors.email}</span>}
                  </div>
                </div>

                <div className="input-group">
                  <div className="input-container">
                    <div className="cadastro-password-field">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        aria-label="Senha"
                        placeholder="Senha (mínimo de 6 caracteres)"
                        value={password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        required
                        minLength="6"
                        maxLength="72"
                        className={errors.password ? 'error' : ''}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="cadastro-password-toggle"
                        onClick={() => setShowPassword((visible) => !visible)}
                        aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                        title={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                      >
                        <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                      </button>
                    </div>
                    {errors.password && <span className="error-text">{errors.password}</span>}
                  </div>
                </div>

                <button type="submit" className="submit-btn" disabled={loading}>
                  {loading ? (
                    <>
                      <div className="btn-spinner"></div>
                      Criando conta...
                    </>
                  ) : (
                    <>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="8.5" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                        <path d="M20 8V14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M23 11H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Criar Conta Gratuita
                    </>
                  )}
                </button>

                {message && (
                  <div className={`message ${message.includes('sucesso') ? 'success' : 'error'}`}>
                    {message}
                  </div>
                )}

                {emailAlreadyRegistered && (
                  <div className="message error cadastro-existing-account" role="alert">
                    <span>Este e-mail já possui uma conta.</span>
                    <ScrollLink to={`/login?email=${encodeURIComponent(email.trim())}`}>
                      Entrar no sistema
                    </ScrollLink>
                  </div>
                )}

                <div className="form-footer">
                  <p>Já tem uma conta? <ScrollLink to="/login">Faça login</ScrollLink></p>
                </div>
              </form>
            </div>
          </div>

          {/* Informações e Vantagens */}
          <div className="cadastro-info-section">
            <div className="info-card">
              <div className="info-header">
                <h2>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                    <path d="M23 21V19C23 18.1645 22.7155 17.3541 22.2094 16.6977C21.7033 16.0414 20.9987 15.5755 20.2 15.3687" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89317 18.7122 8.75608 18.1676 9.45768C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Mais de 1,4 milhão de alunos
                </h2>
                <p>
                  Entendemos que a educação online se tornou uma opção cada vez mais popular e acessível. 
                  Estamos comprometidos em fornecer uma experiência de aprendizado de alta qualidade 
                  que atenda às suas necessidades e expectativas.
                </p>
              </div>

              <div className="vantagens-section">
                <h3>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  Vantagens de estudar conosco
                </h3>
                <div className="vantagens-grid">
                  <div className="vantagem-item">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                      <polyline points="12,6 12,12 16,14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>Flexibilidade de horários</span>
                  </div>
                  <div className="vantagem-item">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 3H8C9.06087 3 10.0783 3.42143 10.8284 4.17157C11.5786 4.92172 12 5.93913 12 7V21C12 20.2044 11.6839 19.4413 11.1213 18.8787C10.5587 18.3161 9.79565 18 9 18H2V3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M22 3H16C14.9391 3 13.9217 3.42143 13.1716 4.17157C12.4214 4.92172 12 5.93913 12 7V21C12 20.2044 12.3161 19.4413 12.8787 18.8787C13.4413 18.3161 14.2044 18 15 18H22V3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>Variedade de cursos</span>
                  </div>
                  <div className="vantagem-item">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                      <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>Economia de tempo e dinheiro</span>
                  </div>
                  <div className="vantagem-item">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4 7V4C4 3.44772 4.44772 3 5 3H19C19.5523 3 20 3.44772 20 4V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <rect x="3" y="7" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="2"/>
                      <path d="M12 11L9 14L15 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>Certificação digital</span>
                  </div>
                  <div className="vantagem-item">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 1V23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M17 5H9.5A3.5 3.5 0 006 8.5V8.5A3.5 3.5 0 009.5 12H14.5A3.5 3.5 0 0118 15.5V15.5A3.5 3.5 0 0114.5 19H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>Aulas 100% gratuitas</span>
                  </div>
                  <div className="vantagem-item">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    <span>Totalmente livre de mensalidades</span>
                  </div>
                  <div className="vantagem-item">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M22 12H18L15 21L9 3L6 12H2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>Ideal para horas extra-curriculares</span>
                  </div>
                  <div className="vantagem-item">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M14 2H6A2 2 0 004 4V20A2 2 0 006 22H18A2 2 0 0020 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <polyline points="10,9 9,9 8,9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>Pré-Requisito para concursos públicos</span>
                  </div>
                </div>
              </div>

              <div className="trust-section">
                <p>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22S8 18 8 13V6L12 4L16 6V13C16 18 12 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <strong>Seus dados estão seguros.</strong> Nunca compartilhamos suas informações com terceiros.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cadastro;
